import { useState } from "react";
import { Gauge, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const section = (t, name) => {
  const m = t.match(new RegExp(`${name}:\\s*([\\s\\S]*?)(?=\\n\\s*[A-Z]{4,}:|$)`));
  return m ? m[1].split("\n").map((l) => l.replace(/^\s*[-•*]\s*/, "").trim()).filter(Boolean) : [];
};

export const parseMatch = (t) => {
  const s = t.match(/SCORE:\s*(\d{1,3})/);
  return { score: s ? Math.min(100, +s[1]) : null, strengths: section(t, "STRENGTHS"), gaps: section(t, "GAPS"), tips: section(t, "TIPS") };
};

const tone = (n) => (n >= 80 ? ["#15803D", "Strong match"] : n >= 60 ? ["#B45309", "Good, with gaps"] : ["#D94E28", "Needs work"]);

const Ring = ({ score }) => {
  const [color, label] = tone(score);
  const c = 2 * Math.PI * 42;
  return (
    <div className="flex items-center gap-5" data-testid="match-score">
      <svg width="104" height="104" viewBox="0 0 104 104" className="-rotate-90">
        <circle cx="52" cy="52" r="42" fill="none" stroke="#F4F0E8" strokeWidth="10" />
        <circle cx="52" cy="52" r="42" fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - score / 100)} style={{ transition: "stroke-dashoffset .8s ease" }} />
      </svg>
      <div>
        <p className="font-serif text-5xl text-ink" data-testid="match-score-value">{score}<span className="text-xl text-taupe">/100</span></p>
        <p className="text-sm font-semibold" style={{ color }}>{label}</p>
      </div>
    </div>
  );
};

const List = ({ title, items, testId, dot }) => items.length > 0 && (
  <div data-testid={testId}>
    <p className="eyebrow mb-2">{title}</p>
    <ul className="space-y-1.5 text-sm text-ink">
      {items.map((x, i) => <li key={i} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: dot }} />{x}</li>)}
    </ul>
  </div>
);

export const MatchDialog = ({ api, data }) => {
  const [open, setOpen] = useState(false);
  const [posting, setPosting] = useState("");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const r = parseMatch(out);

  const run = async () => {
    setBusy(true);
    setOut("");
    try { await api.stream("/ai/match", { resume: data, job_posting: posting }, setOut); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button className="btn-ink" onClick={() => setOpen(true)} data-testid="match-score-btn"><Gauge className="h-4 w-4 text-thread" /> Match score</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-paper-border bg-paper" data-testid="match-dialog">
          <DialogHeader>
            <p className="eyebrow">Job match score</p>
            <DialogTitle className="font-serif text-2xl font-medium">How well does this resume fit the job?</DialogTitle>
            <DialogDescription>Paste a job posting to get a score out of 100, what's working, what's missing and what to change.</DialogDescription>
          </DialogHeader>
          <Textarea className="field min-h-[140px]" placeholder="Paste the full job posting here…" value={posting} onChange={(e) => setPosting(e.target.value)} data-testid="match-posting-input" />
          <button className="btn-thread" onClick={run} disabled={busy || posting.trim().length < 20} data-testid="match-run-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gauge className="h-4 w-4" />} {busy ? "Scoring…" : out ? "Score again" : "Score my resume"}
          </button>
          {(busy || out) && (
            <div className="space-y-5 rounded-xl border border-paper-border bg-white p-5" data-testid="match-result">
              {r.score != null ? <Ring score={r.score} /> : <p className="caret text-sm text-ink-soft">Reading the posting</p>}
              <List title="What's working" items={r.strengths} testId="match-strengths" dot="#15803D" />
              <List title="Missing or weak" items={r.gaps} testId="match-gaps" dot="#D94E28" />
              <List title="Make these changes" items={r.tips} testId="match-tips" dot="#0F172A" />
              {!busy && r.score == null && <p className="text-xs text-red-600">Could not read the score. Please try again.</p>}
              {!busy && r.tips.length > 0 && <p className="text-xs text-taupe">Tip: use "Tailor to job" with the same posting to apply many of these changes automatically.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
